import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Field from '../../components/forms/Field';
import {
  emailFormStructure,
  runnerFormStructure,
  emergencyFormStructure,
} from '../../data/registrationStructure';
import { applyVisibility } from '../../utils/formVisibility';
import { z } from 'zod';

import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';

import { zodResolver } from '@hookform/resolvers/zod';
import { registrationSchema } from '../../validation/registrationSchema';
import { readJson } from '../../utils/generateToken';
import { useSizes } from '../../hooks/useSize';

const SUMMARY_PATH = '/event/register/indomaret/summary';

const RADIUS_PX = 12;
const SECTION_BORDER = '#D1D5DB';

const STATIC_SIZE_OPTIONS =
  runnerFormStructure.find((field) => field.key === 'size')?.options || [];

function SizeChartDialog({ open, onClose, imgSrc, title = 'Size Chart' }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ fontWeight: 800 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box
          sx={{
            maxHeight: {
              xs: 'calc(100dvh - 160px)',
              sm: 'calc(100dvh - 180px)',
            },
            overflow: 'auto',
          }}
        >
          {imgSrc ? (
            <img
              src={imgSrc}
              alt="Size chart"
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          ) : (
            <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
              Size chart image is not available.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const SizeOption = z.object({ value: z.string(), label: z.string() });
const SizeOptionLoose = z.union([
  SizeOption,
  z.string().length(0),
  z.null(),
  z.undefined(),
]);

const isISO_YMD = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s);
const toUTCDate = (s) => new Date(`${s}T00:00:00Z`);
const fmtDMY = (iso) => {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};

const normalizeRaceNameToEnum = (nameOrId) => {
  if (typeof nameOrId === 'string') {
    const s = nameOrId.trim().toLowerCase();
    if (s.includes('5')) return '5k';
    if (s.includes('10')) return '10k';
    if (s.includes('21')) return '21k';
  }
  if (typeof nameOrId === 'number') {
    if (nameOrId === 229) return '5k';
    if (nameOrId === 231) return '21k';
    if (nameOrId === 230) return '10k';
  }
  return undefined;
};

const isoDateOnly = (dLike) => {
  try {
    if (!dLike) return undefined;
    const d = new Date(dLike);
    if (Number.isNaN(d.getTime())) return undefined;
    return d.toISOString().slice(0, 10);
  } catch {
    return undefined;
  }
};

function gridPropsFromWidth(width) {
  let cols = 12;
  if (width === true || width == null || width === '1') cols = 12;
  else if (typeof width === 'number') cols = Math.max(1, Math.min(12, width));
  else if (typeof width === 'string' && width.includes('/')) {
    const [n, d] = width.split('/').map(parseFloat);
    if (Number.isFinite(n) && Number.isFinite(d) && d > 0)
      cols = Math.round((n / d) * 12);
  }
  return { xs: 12, sm: 12, md: cols, lg: cols };
}

function Section({ title, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: `${RADIUS_PX}px`,
        borderColor: SECTION_BORDER,
        borderWidth: 3,
        p: { xs: 2, md: 3 },
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        {title}
      </Typography>
      {children}
    </Paper>
  );
}

export default function IndomaretRegistration() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { item } = useParams();

  // Option A (Edit via navigate state)
  const initialValues = state?.initialValues || null;
  const meta = state?.meta || null;

  // Decode `item` jika bukan edit mode
  const [ev, setEv] = useState(null);
  const [decodeError, setDecodeError] = useState(null);

  const [regionData, setRegionData] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionsError, setRegionsError] = useState(null);

  //load region
  useEffect(() => {
    let active = true;

    const loadRegions = async () => {
      try {
        setRegionsLoading(true);
        setRegionsError(null);

        const response = await fetch('/data/indonesia-regions.json');

        if (!response.ok) {
          throw new Error('Gagal memuat data wilayah');
        }

        const result = await response.json();

        if (!active) return;

        setRegionData(Array.isArray(result) ? result : []);
      } catch (error) {
        if (!active) return;

        setRegionData([]);
        setRegionsError(error);
      } finally {
        if (active) {
          setRegionsLoading(false);
        }
      }
    };

    loadRegions();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    // console.log('use effect, initial Values : ', initialValues);
    // console.log('use effect, item : ', item);
    if (initialValues || meta) return;
    if (!item) return;
    try {
      const fromRead = JSON.parse(readJson(item));
      // console.log('useEffect fromRead ', fromRead);
      setEv(fromRead);
      setDecodeError(null);
    } catch {
      setDecodeError('Data registrasi tidak valid. Silakan buka link ulang.');
    }
  }, [item, initialValues, meta]);

  const [sizeChartOpen, setSizeChartOpen] = useState(false);
  const [beltChartOpen, setBeltChartOpen] = useState(false);
  const [jacketChartOpen, setJacketChartOpen] = useState(false);
  const [selectedChart, setselectedChart] = useState('jersey');

  const sizeChartImgSrc = '/jersey_size.jpeg';

  const beltChartImgSrc = '/belt_size.jpeg';

  const jacketChartImgSrc = '/jakcet_size.jpeg';

  // Event & Category
  const eventId = meta?.eventId ?? ev?.eventId ?? null;
  const categoryId = meta?.category_id ?? ev?.category?.race_category ?? null;

  // Sizes
  const hasStaticSizeOptions = STATIC_SIZE_OPTIONS.length > 0;
  const { sizes: databaseSizes, loading: databaseSizesLoading } = useSizes({
    eventId,
    categoryId,
    enabled: !hasStaticSizeOptions,
  });

  const sizes = useMemo(() => {
    if (hasStaticSizeOptions) {
      return STATIC_SIZE_OPTIONS;
    }

    return Array.isArray(databaseSizes) ? databaseSizes : [];
  }, [hasStaticSizeOptions, databaseSizes]);

  const sizesLoading = !hasStaticSizeOptions && databaseSizesLoading;

  // === NEW: flags untuk validasi pre-submit ===
  const sizeReady = sizes.length > 0;
  const sizeLoadFailed = !hasStaticSizeOptions && !sizesLoading && !sizeReady;

  // Birth-date limit & reg close
  const birthMaxFromServer = isoDateOnly(ev?.category?.maxAllowedDate);
  // console.log('end reg ', ev?.category?.endReg);
  // console.log('end reg ', new Date(ev.category.endReg));
  const regClosed = ev?.category?.endReg
    ? new Date(ev.category.endReg) < new Date()
    : false;

  // console.log(ev?.category?.endReg || null);
  // console.log(regClosed);

  /* ---------- race_category PRIORITY ---------- */
  const rawRace =
    initialValues?.race_category ??
    state?.race_category ??
    ev?.category?.name ??
    normalizeRaceNameToEnum(ev?.category?.race_category) ??
    '10k';
  const normalizedRace = normalizeRaceNameToEnum(rawRace) || '10k';

  // RHF + resolver dinamis
  const allowedRef = useRef(new Set());
  useEffect(() => {
    allowedRef.current = new Set((sizes || []).map((o) => String(o.value)));
  }, [sizes]);

  const methods = useForm({
    resolver: async (values, ctx, opts) => {
      const hasSizeOptions = Array.isArray(sizes) && sizes.length > 0;

      const dynamicSizeSchema = z.object({
        size: SizeOptionLoose.superRefine((opt, issue) => {
          if (!hasSizeOptions) return; // kalau belum ada opsi size, jangan blokir (guard di tombol)
          const allowed = allowedRef.current;
          if (!opt || typeof opt === 'string') {
            issue.addIssue({
              code: 'custom',
              message: 'Select your jersey size',
            });
            return;
          }
          if (!allowed.has(String(opt.value))) {
            issue.addIssue({
              code: 'custom',
              message: 'Size tidak tersedia/invalid',
            });
          }
          const metaOpt = sizes.find((o) => o.value === opt.value);
          if (metaOpt?.disabled) {
            issue.addIssue({ code: 'custom', message: 'Size ini sudah habis' });
          }
        }),
      });

      const birthMaxISO = birthMaxFromServer || '2011-12-31';
      const dynamicBirthSchema = z.object({
        brith_date: z
          .string()
          .min(1, 'Tanggal lahir wajib diisi')
          .refine(isISO_YMD, 'Format tanggal tidak valid (yyyy-mm-dd)')
          .refine((v) => toUTCDate(v) <= toUTCDate(birthMaxISO), {
            message: `Max brith date allowed is ${fmtDMY(birthMaxISO)}`,
          }),
      });

      const regionOptionSchema = z
        .union([
          z.object({
            label: z.string().min(1),
            value: z.string().min(1),
          }),
          z.string().min(1),
        ])
        .transform((value) =>
          typeof value === 'string'
            ? {
                label: value,
                value,
              }
            : value,
        );

      const dynamicRegionSchema = z
        .object({
          province: regionOptionSchema,
          city_regency: regionOptionSchema,
        })
        .superRefine((value, issueCtx) => {
          const provinceData = regionData.find(
            (item) => item.value === value.province.value,
          );

          if (!provinceData) {
            issueCtx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['province'],
              message: 'Province is not valid.',
            });
            return;
          }

          const cityRegencyValid = (provinceData.regencies || []).some(
            (item) => item.value === value.city_regency.value,
          );

          if (!cityRegencyValid) {
            issueCtx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['city_regency'],
              message: 'The city/regency does not match the selected province.',
            });
          }
        });

      const combined = registrationSchema
        .and(dynamicSizeSchema)
        .and(dynamicBirthSchema)
        .and(dynamicRegionSchema);

      return zodResolver(combined)(values, ctx, opts);
    },
    defaultValues: {
      race_category: normalizedRace,
      email: '',
      repeat_email: '',
      name: '',
      contact: '',
      gender: null,
      nationality: null,
      address: null,
      province: null,
      city_regency: null,
      id_type: null,
      id_number: '',
      brith_date: '',
      blood_type: null,
      org_kom: '',
      org_com: '',
      rpt_10: undefined,
      rpt_21: undefined,
      exp_fin_time: { hour: 0, minute: 0, second: 0 },
      medical_note: '',
      emergency_person: '',
      emergency_contact: '',
      emergency_relation: null,
      size: '',
      belt_size: '',
      finisher_size: '',
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    watch,
    setValue,
    resetField,
    getValues,
    trigger,
    setFocus,
    formState,
  } = methods;

  //#region province
  const selectedProvince = watch('province');

  const provinceOptions = useMemo(() => {
    return regionData.map((province) => ({
      label: province.label,
      value: province.value,
    }));
  }, [regionData]);

  const cityRegencyOptions = useMemo(() => {
    if (!selectedProvince?.value) return [];

    const province = regionData.find(
      (item) => item.value === selectedProvince.value,
    );

    return province?.regencies || [];
  }, [regionData, selectedProvince?.value]);

  const previousProvinceRef = useRef(null);

  useEffect(() => {
    const currentProvinceValue = selectedProvince?.value || null;

    if (previousProvinceRef.current === null) {
      previousProvinceRef.current = currentProvinceValue;
      return;
    }

    if (previousProvinceRef.current !== currentProvinceValue) {
      setValue('city_regency', null, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    previousProvinceRef.current = currentProvinceValue;
  }, [selectedProvince?.value, setValue]);
  //#endregion

  // Prefill saat edit
  useEffect(() => {
    if (!initialValues) return;
    methods.reset(initialValues, { keepDefaultValues: false });
  }, [initialValues, methods]);

  // Set race_category ke form supaya visibility jalan (tidak menandai dirty)
  useEffect(() => {
    setValue('race_category', normalizedRace, {
      shouldValidate: true,
      shouldDirty: false,
    });
  }, [normalizedRace, setValue]);

  // Pulihkan pilihan size (edit) setelah opsi size loaded
  useEffect(() => {
    const iv = initialValues?.size;
    if (!iv || !sizes?.length) return;
    const wanted = typeof iv === 'string' ? iv : iv.value;
    const opt = sizes.find((o) => o.value === wanted);
    if (opt) {
      methods.setValue('size', opt, {
        shouldValidate: true,
        shouldDirty: false,
      });
    }
  }, [sizes, initialValues, methods]);

  // Jaga size saat kategori/opsi berubah
  useEffect(() => {
    const cur = methods.getValues('size');
    const curVal = cur && typeof cur === 'object' ? cur.value : '';
    const opt = (sizes || []).find((o) => o.value === curVal);
    const invalid = !curVal || !opt || !!opt?.disabled;
    if (invalid) {
      methods.resetField('size', { defaultValue: '' });
      if (methods.formState.errors.size) methods.clearErrors('size');
    }
  }, [categoryId, sizes, methods]);

  // Revalidate repeat_email kalau email berubah
  const email = watch('email');
  const repeatEmail = watch('repeat_email');
  useEffect(() => {
    const isEmail = (s) => /\S+@\S+\.\S+/.test(s || '');
    if (repeatEmail && isEmail(email)) trigger('repeat_email');
  }, [email, repeatEmail, trigger]);

  const values = watch();

  // Visibility + inject birth limit + inject size options
  const visibleRunner = useMemo(
    () => applyVisibility(runnerFormStructure, values),
    [values],
  );

  const runnerWithBirthLimit = useMemo(() => {
    const base = !birthMaxFromServer
      ? visibleRunner
      : visibleRunner.map((f) =>
          f.key === 'brith_date'
            ? { ...f, min: f.min || '1900-01-01', max: birthMaxFromServer }
            : f,
        );
    return base.map((f) =>
      f.key === 'size'
        ? {
            ...f,
            type: 'select',
            required: true,
            placeholder: sizesLoading
              ? 'Memuat size...'
              : sizeReady
                ? 'Pilih size'
                : 'Size belum tersedia',
            options: (sizes || []).map((o) => ({
              value: o.value,
              label: o.label,
              disabled: !!o.disabled,
            })),
            disabled: !!sizesLoading || !sizeReady,
          }
        : f,
    );
  }, [visibleRunner, birthMaxFromServer, sizes, sizesLoading, sizeReady]);

  const runnerWithLocationOptions = useMemo(() => {
    return runnerWithBirthLimit.map((field) => {
      if (field.key === 'province') {
        return {
          ...field,
          options: provinceOptions,
          disabled: regionsLoading,
          placeholder: regionsLoading
            ? 'Memuat Provinsi...'
            : 'Pilih Provinsi...',
        };
      }

      if (field.key === 'city_regency') {
        return {
          ...field,
          options: cityRegencyOptions,
          disabled:
            regionsLoading ||
            !selectedProvince?.value ||
            cityRegencyOptions.length === 0,
          placeholder: regionsLoading
            ? 'Memuat Kota / Kabupaten...'
            : !selectedProvince?.value
              ? 'Pilih Provinsi terlebih dahulu'
              : 'Pilih Kota / Kabupaten...',
        };
      }

      return field;
    });
  }, [
    runnerWithBirthLimit,
    provinceOptions,
    cityRegencyOptions,
    regionsLoading,
    selectedProvince?.value,
  ]);

  // Bersihkan nilai field yang hidden
  useEffect(() => {
    const visibleKeys = new Set(
      runnerWithLocationOptions.map((field) => field.key),
    );

    runnerFormStructure.forEach((field) => {
      if (!visibleKeys.has(field.key) && getValues(field.key) !== undefined) {
        resetField(field.key, {
          defaultValue: undefined,
        });
      }
    });
  }, [runnerWithLocationOptions, getValues, resetField]);

  // Submit
  const onSubmit = (data) => {
    const nextMeta = {
      eventId,
      coupon: meta?.coupon ?? ev?.coupon ?? null,
      category_id: categoryId,
    };
    navigate(SUMMARY_PATH, {
      replace: true,
      state: { values: data, meta: nextMeta },
    });
  };

  // === NEW: guard sebelum submit ===
  const onPreSubmit = (data) => {
    if (!sizeReady) {
      methods.setError('size', {
        type: 'manual',
        message: 'Ukuran jersey belum berhasil dimuat. Silakan coba lagi.',
      });
      setFocus('size');
      return; // tahan navigasi
    }
    onSubmit(data);
  };

  // Tangkap invalid → fokus ke error pertama
  const onInvalid = (errors) => {
    const first = Object.keys(errors || {})[0];
    if (first) setFocus(first);
  };

  const handleSizeChartOpen = (key) => {
    setselectedChart(key);
    setSizeChartOpen(true);
  };

  const showDecodeError = !!decodeError && !initialValues && !meta;

  if (sizesLoading)
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mt: { xs: 8, sm: 5 },
        }}
      >
        <CircularProgress size="4rem" />
      </Box>
    );

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          width: '100%',
          maxWidth: '56rem',
          mx: 'auto',
          px: { xs: 2, md: 0 },
          mt: { xs: 3, md: 5, xl: 8 },
          py: { xs: 5, sm: 3, md: 0 },
        }}
      >
        {showDecodeError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {decodeError}
          </Alert>
        ) : null}

        {regionsError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Filed to load province and city/regency data. Please refresh the
            page.
          </Alert>
        ) : null}

        {/* NEW: warning jika gagal memuat size */}
        {sizeLoadFailed ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Gagal memuat daftar size dari server. Mohon periksa koneksi Anda
            lalu coba lagi.
          </Alert>
        ) : null}

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              border: '3px solid',
              borderColor: SECTION_BORDER,
              borderRadius: `${RADIUS_PX}px`,
              px: 2,
              py: 1,
              width: 'fit-content',
            }}
          >
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, color: 'text.secondary' }}
            >
              KATEGORI
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 700, textTransform: 'uppercase' }}
            >
              {ev?.category?.name || values.race_category || '-'}
            </Typography>
          </Box>

          {(meta?.coupon || ev?.coupon)?.voucher ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                border: '3px dashed',
                borderColor: SECTION_BORDER,
                borderRadius: `${RADIUS_PX}px`,
                px: 2,
                py: 1,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Voucher:
              </Typography>
              <Typography variant="body2">
                {(meta?.coupon || ev?.coupon)?.voucher}
              </Typography>
            </Box>
          ) : null}
        </Box>

        <Box
          component="form"
          noValidate
          onSubmit={handleSubmit(onPreSubmit, onInvalid)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Stack spacing={3}>
            <Section title="Email Info">
              <Grid container spacing={2}>
                {emailFormStructure.map((f) => (
                  <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                    <Field field={f} />
                  </Grid>
                ))}
              </Grid>
            </Section>

            <Section title="Runner Info">
              <Grid container spacing={2}>
                {runnerWithLocationOptions.map((f) =>
                  f.key === 'size' ||
                  f.key === 'belt_size' ||
                  f.key === 'finisher_size' ? (
                    <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 1,
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Field field={f} />
                        </Box>

                        <Button
                          variant="outlined"
                          onClick={() => handleSizeChartOpen(f.key)}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          {f.key === 'size'
                            ? 'Race Appeal'
                            : f.key === 'belt_size'
                              ? 'Belt Appeal'
                              : 'Jacket Appeal'}
                        </Button>
                      </Box>
                    </Grid>
                  ) : (
                    <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                      <Field field={f} />
                    </Grid>
                  ),
                )}
              </Grid>
            </Section>

            <Section title="Emergency Info">
              <Grid container spacing={2}>
                {emergencyFormStructure.map((f) => (
                  <Grid key={f.key} item {...gridPropsFromWidth(f.width)}>
                    <Field field={f} />
                  </Grid>
                ))}
              </Grid>
            </Section>
          </Stack>

          <Box
            sx={{
              pt: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            {formState.isSubmitted && !formState.isValid ? (
              <Typography sx={{ color: '#d32f2f' }}>
                Please fix the highlighted fields.
              </Typography>
            ) : (
              <span />
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={
                regClosed ||
                sizesLoading ||
                !sizeReady ||
                regionsLoading ||
                !!regionsError
              } // NEW regClosed ||
              sx={{
                bgcolor: '#d32f2f',
                '&:hover': { bgcolor: '#b71c1c' },
                borderRadius: 1,
                px: 5,
                py: 1.25,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {regClosed
                ? 'Pendaftaran Ditutup'
                : sizesLoading
                  ? 'Memuat Size...'
                  : !sizeReady
                    ? 'Size Belum Siap'
                    : 'Go To Summary'}
            </Button>
          </Box>
        </Box>
      </Box>

      <SizeChartDialog
        open={sizeChartOpen}
        onClose={() => setSizeChartOpen(false)}
        imgSrc={
          selectedChart && selectedChart === 'size'
            ? sizeChartImgSrc
            : selectedChart === 'belt_size'
              ? beltChartImgSrc
              : jacketChartImgSrc
        }
        title={`${selectedChart && selectedChart === 'size' ? 'Race' : selectedChart === 'belt_size' ? 'Belt' : 'Jacket'} Appeal — Size Chart`}
      />
    </FormProvider>
  );
}
